import { useEffect, useState } from "react";
import { feefoApi, ApiError } from "@/api";
import type { FeefoJobSearchResult, FeefoRequest } from "@/types";
import { Button, Spinner } from "@/components/ui";
import { FormDrawer } from "@/staff/jobs/FormDrawer";
import { formatRelative } from "@/lib/format";
import { FeefoRequestRow, NewRequestDrawer } from "./feedbackShared";
import "./feedback.css";

const DAY_MS = 86_400_000;

/**
 * Standalone two-step "New feedback request" flow (from the Feedback section
 * header). Step 1 searches for a job and, once picked, shows its previous
 * requests (same rows as the job-detail tab). Step 2 is the confirm-send drawer,
 * pre-populated from the chosen job's resolved client. onCreated fires after a
 * successful send (the page refreshes + closes).
 */
export function FeedbackRequestFlow({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [job, setJob] = useState<FeefoJobSearchResult | null>(null);
  const [prev, setPrev] = useState<FeefoRequest[] | null>(null);
  const [step, setStep] = useState<1 | 2>(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Load the picked job's previous requests (for the preview + predicted ref).
  useEffect(() => {
    if (!job) {
      setPrev(null);
      return;
    }
    let cancelled = false;
    setPrev(null);
    feefoApi
      .listFeefoRequests(job.job_id)
      .then((res) => {
        if (!cancelled) setPrev(res.items);
      })
      .catch(() => {
        if (!cancelled) setPrev([]);
      });
    return () => {
      cancelled = true;
    };
  }, [job]);

  async function send(description: string, stageNote: string) {
    if (!job) return;
    setBusy(true);
    setError(undefined);
    try {
      await feefoApi.createFeefoRequest({
        job_id: job.job_id,
        milestone: "manual",
        description,
        stage_note: stageNote,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not send the request.");
      setBusy(false); // keep the drawer open so the error is visible
    }
  }

  // Step 2 — confirm + send for the chosen job.
  if (step === 2 && job) {
    const rows = prev ?? [];
    const nextSeq = (rows.length ? Math.max(...rows.map((r) => r.request_seq)) : 0) + 1;
    const lastSentAt = rows.length ? rows[0].sent_at : 0;
    return (
      <NewRequestDrawer
        clientName={job.client_name}
        clientEmail={job.client_email}
        predictedOrderRef={`${job.job_number}-${nextSeq}`}
        recentlyAsked={lastSentAt > 0 && Date.now() - lastSentAt < 7 * DAY_MS}
        lastAskedLabel={formatRelative(lastSentAt)}
        busy={busy}
        cancelLabel="Back"
        onCancel={() => {
          if (!busy) setStep(1);
        }}
        onConfirm={send}
      />
    );
  }

  // Step 1 — job picker + previous-requests preview.
  return (
    <JobPickerStep
      job={job}
      prev={prev}
      error={error}
      onError={setError}
      onSelect={setJob}
      onClose={onClose}
      onNext={() => setStep(2)}
    />
  );
}

function JobPickerStep({
  job,
  prev,
  error,
  onError,
  onSelect,
  onClose,
  onNext,
}: {
  job: FeefoJobSearchResult | null;
  prev: FeefoRequest[] | null;
  error: string | undefined;
  onError: (msg: string | undefined) => void;
  onSelect: (job: FeefoJobSearchResult) => void;
  onClose: () => void;
  onNext: () => void;
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<FeefoJobSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  // Debounced search; needs ≥2 chars (matches the server's minimum).
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setResults([]);
      setSearched(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      feefoApi
        .searchFeedbackJobs(term)
        .then((res) => {
          if (!cancelled) {
            setResults(res.items);
            setSearched(true);
          }
        })
        .catch((err) => {
          if (!cancelled) onError(err instanceof ApiError ? err.message : "Could not search jobs.");
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q, onError]);

  const canNext = !!job && job.has_email;

  return (
    <FormDrawer
      title="New feedback request — choose a job"
      onClose={onClose}
      closeOnOverlayClick={false}
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="button"
            disabled={!canNext}
            onClick={onNext}
            title={job && !job.has_email ? "This job's client has no email address." : undefined}
          >
            Next
          </Button>
        </>
      }
    >
      <div className="feefo-flow">
        <p className="job-drawer__muted" style={{ margin: 0 }}>
          Search for the job you want to request feedback on.
        </p>
        <input
          className="leads__search"
          type="search"
          placeholder="Search jobs by name, address or postcode…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus
        />

        {error ? (
          <p className="leads__error" role="alert" onClick={() => onError(undefined)}>
            {error}
          </p>
        ) : null}

        {loading ? (
          <div className="jobs-list__loading">
            <Spinner label="Searching jobs…" />
          </div>
        ) : q.trim().length >= 2 && searched && results.length === 0 ? (
          <p className="job-drawer__muted">No jobs found — try a different search.</p>
        ) : results.length > 0 ? (
          <ul className="job-client-results">
            {results.map((j) => {
              const active = job?.job_id === j.job_id;
              return (
                <li key={j.job_id}>
                  <button
                    type="button"
                    className={`job-client-result${active ? " feefo-flow__pick--active" : ""}`}
                    onClick={() => onSelect(j)}
                  >
                    <span className="job-client-result__name">
                      <span className="numeric">{j.job_number}</span> · {j.job_name || "Untitled job"}
                    </span>
                    <span className="job-client-result__email">
                      {j.client_name || "No client"}
                      {j.client_email ? ` · ${j.client_email}` : " · no email"}
                      {j.full_address ? ` · ${j.full_address}` : ""}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}

        {job ? (
          <div className="feefo-flow__selected">
            <div className="feefo-flow__selected-head">
              <strong>
                <span className="numeric">{job.job_number}</span> · {job.job_name || "Untitled job"}
              </strong>
              <div className="job-drawer__muted">
                {job.client_name || "No client"}
                {job.client_email ? ` · ${job.client_email}` : ""}
              </div>
            </div>

            {!job.has_email ? (
              <p className="feefo__hint">
                This job's client has no email address — pick another job, or add an email on the job's Clients tab.
              </p>
            ) : null}

            <div className="feefo-flow__prev-title">Previous requests</div>
            {prev === null ? (
              <div className="jobs-list__loading">
                <Spinner label="Loading…" />
              </div>
            ) : prev.length === 0 ? (
              <p className="job-drawer__muted">No previous requests for this job.</p>
            ) : (
              <div className="feefo__list">
                {prev.map((r) => (
                  <FeefoRequestRow key={r.id} r={r} />
                ))}
              </div>
            )}
          </div>
        ) : null}
      </div>
    </FormDrawer>
  );
}
